import { HttpParams } from '@angular/common/http';
import subHours from 'date-fns/subHours';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import subMinutes from 'date-fns/subMinutes';
import endOfDay from 'date-fns/endOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import pickBy from 'lodash-es/pickBy';
import identity from 'lodash-es/identity';
import isEqual from 'lodash-es/isEqual';
import { extractRawUrl, toWebSocketUrl } from '@zerops/zef/utils';
import { TrLogPeriods } from './trlog.constant';
import { FormatOptions, TrLogParams, TrLogUrlParams } from './trlog.model';

/**
 * ANSI escape sequences, which arrive verbatim in log content: a process writing to
 * the container's stdout has no TTY to detect, so plenty of tooling colours its
 * output unconditionally. Rendering that as text shows the parameter bytes ("[32m",
 * "[38;5;3m") plus an unprintable box where the ESC byte sits.
 *
 * Three alternatives are matched:
 *  - CSI, `ESC [` params intermediates final, per ECMA-48. Colour (SGR) codes are the
 *    ones that actually show up, but cursor moves and erases share the same shape.
 *  - CSI in its single-byte C1 form (U+009B), which some producers emit instead.
 *  - OSC, `ESC ]` up to BEL or ST, used for hyperlinks and window titles. Its body is
 *    arbitrary text, so it has to be consumed as a unit rather than left behind.
 *
 * Written with unicode escapes rather than literal control bytes so the pattern stays
 * readable and survives copy/paste.
 */
// Matching control characters is the entire purpose of this pattern.
// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE = /\u001B\[[0-?]*[ -/]*[@-~]|\u009B[0-?]*[ -/]*[@-~]|\u001B\][^\u0007\u001B]*(?:\u0007|\u001B\\)?/g;

/**
 * Remove ANSI escape sequences from a log line.
 *
 * Applied where log items enter the store rather than in the template, so the rendered
 * text, the client-side content filter and copy-to-clipboard all operate on the same
 * string - stripping at render time would leave the escapes in copied output and let
 * them match search terms invisibly.
 */
export const stripAnsi = (value: string): string => value
  ? value.replace(ANSI_ESCAPE, '')
  : value;

export const getTrLogEndpoint = (
  url: string,
  protocol: 'HTTP' | 'WS' = 'HTTP'
): string => {
  if (!url) return '';

  if (protocol === 'HTTP') {
    const rawUrl = extractRawUrl(url);
    // Legacy format doesn't include protocol, modern does
    return rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
  }

  return toWebSocketUrl(url, '/stream');
}

export const addTrLogParamsToUrl = (
  url: string,
  params: TrLogUrlParams
) => {
  const queryParams = new HttpParams({ fromObject: pickBy({
    ...params,
    desc: params.desc ? params.desc : '0'
  }, identity) as any });
  return `${url}&${queryParams?.toString()}`;
}

export const addContainerIdToUrl = (
  url: string,
  containerId: string
) => {
  return `${url}&containerId=${containerId}`;
}

export const periodSettings = {
  [TrLogPeriods.Today]: () => ({
    from: startOfDay(new Date()),
    till: new Date()
  }),
  [TrLogPeriods.Yesterday]: () => {
    const now = new Date();
    return {
      from: startOfDay(subDays(now, 1)),
      till: endOfDay(subDays(now, 1))
    };
  },
  [TrLogPeriods.Last15Minutes]: () => ({
    from: subMinutes(new Date(), 15),
    till: new Date()
  }),
  [TrLogPeriods.Last60Minutes]: () => ({
    from: subMinutes(new Date(), 60),
    till: new Date()
  }),
  [TrLogPeriods.Last7Hours]: () => ({
    from: subHours(new Date(), 7),
    till: new Date()
  }),
  [TrLogPeriods.Last24Hours]: () => ({
    from: subHours(new Date(), 24),
    till: new Date()
  }),
  [TrLogPeriods.Last7Days]: () => ({
    from: subDays(new Date(), 7),
    till: new Date()
  }),
  [TrLogPeriods.ThisWeek]: () => ({
    from: startOfWeek(new Date()),
    till: new Date()
  }),
  [TrLogPeriods.LastWeek]: () => {
    const now = new Date();
    return {
      from: startOfWeek(subDays(now, 7)),
      till: endOfWeek(subDays(now, 7))
    };
  }
};

export const deepCompare = (x: any, y: any) => isEqual(x, y);

export const toStateApiParams = (params: TrLogParams): TrLogUrlParams  => {

  let trLogUrlParams: TrLogUrlParams;

  const base = {
    desc: params.desc === undefined ? '0' : params.desc,
    content: params.content,
    minimumSeverity: params.minimumSeverity,
    serviceStackId: params.serviceStackId,
    containerId: params.containerId,
    projectId: params.projectId,
    facility: !!params?.source ?
      params.source === 'access-logs'
        ? 17
        : 16
      : undefined,
    tags: params.tags
  };

  trLogUrlParams = { ...base };

  if (params.format === FormatOptions.RAW || params.format === FormatOptions.RFC_3164 || params.format === FormatOptions.RFC_5424) {
    trLogUrlParams = {
      ...trLogUrlParams,
      format: params.format,
      showTimestamps: params.showTimestamps
    };
  }

  if (params.mode === 'tail') {
    trLogUrlParams = {
      ...trLogUrlParams,
      desc: '0',
      limit: 500
    };
  }

  if (params.mode === 'absPeriod' || params.mode === 'relPeriod') {

    const period = periodSettings[params.period]();

    trLogUrlParams = {
      ...trLogUrlParams,
      from: period.from.toISOString(),
      till: period.till.toISOString(),
      desc: '0'
    };
  }

  if (params.mode === 'range') {
    trLogUrlParams = {
      ...trLogUrlParams,
      from: params.from,
      till: params.till,
      desc: '0'
    };
  }

  if (params.mode === 'limit') {
    let limit = 1000;
    const paramsLimit = parseInt(params.limit as string, 10);
    if ([ 1000, 5000, 10000 ].includes(paramsLimit)) {
      limit = paramsLimit;
    }

    trLogUrlParams = {
      ...trLogUrlParams,
      limit,
      desc: '0'
    };
  }

  return trLogUrlParams;

}
